import { Dialog } from '@angular/cdk/dialog'
import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  ReactiveFormsModule,
  FormsModule,
  FormBuilder,
  Validators,
} from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { Router, ActivatedRoute } from '@angular/router'
import { firstValueFrom } from 'rxjs'
import { DialogService } from '../../../../services/dialog.service'
import { ErrorService } from '../../../../services/error.service'
import { SpinnerService } from '../../../../services/spinner.service'
import { TranslationService } from '../../../../services/translation.service'
import { ApiError } from '../../../../types/apiError'
import { IngredientCsvTemplate } from '../../../../types/csvTemplates'
import { IngredientCsvTemplatesService } from '../../../../services/ingredientCsvTemplate.service'

@Component({
  selector: 'app-create-ingredient-template',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInputModule,
    MatButtonModule,
    MatIcon,
  ],
  templateUrl: './create-ingredient-template.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-ingredient-template.component.scss',
})
export class CreateIngredientTemplateComponent implements OnInit {
  translationService = inject(TranslationService)
  router = inject(Router)
  private fb = inject(FormBuilder)
  private spinnerService = inject(SpinnerService)
  private dialog = inject(Dialog)
  private dialogService = inject(DialogService)
  private errorService = inject(ErrorService)
  private csvTemplateService = inject(IngredientCsvTemplatesService)
  private route = inject(ActivatedRoute)

  templateForm = this.fb.group({
    templateName: ['', Validators.required],
    name: [this.translationService.getNameText(), Validators.required],
    otherNames: [
      this.translationService.getOtherNamesText(),
      Validators.required,
    ],
    casNumbers: [
      this.translationService.getCasNumbersText(),
      Validators.required,
    ],
  })

  title = this.translationService.getCreateCSVTemplateText()

  isEditMode: boolean = false
  templateId: string | null = null
  templateForEdit?: IngredientCsvTemplate

  ngOnInit(): void {
    this.route.paramMap.subscribe(async (params) => {
      const templateId = params.get('id')
      this.isEditMode = !!templateId

      if (this.isEditMode) {
        this.title = this.translationService.getEditCSVTemplateText()
        await this.setTemplate(templateId!)
      }
    })
  }

  /**
   * Sets the template data for Edit Mode
   * @param {string} templateId Template id from route params
   */
  async setTemplate(templateId: string) {
    try {
      this.templateId = templateId
      this.templateForEdit = await firstValueFrom(
        this.csvTemplateService.getById(this.templateId),
      )

      this.setFields()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Sets UI fields for Edit Mode
   */
  setFields() {
    if (this.templateForEdit) {
      const templateData: Partial<IngredientCsvTemplate> = {
        ...this.templateForEdit,
      }
      this.templateForm.patchValue(templateData)
    }
  }

  async onSubmit() {
    try {
      this.spinnerService.startSpinner()

      if (this.templateForm.valid) {
        const template = this.templateForm.value as IngredientCsvTemplate

        let successMsg = this.translationService.getCSVTemplateCreatedText()
        if (!this.isEditMode) {
          await firstValueFrom(this.csvTemplateService.create(template))
        } else {
          if (!this.templateId) {
            return
          }
          await firstValueFrom(
            this.csvTemplateService.edit(this.templateId, template),
          )
          successMsg = this.translationService.getCSVTemplateUpdatedText()
        }

        this.dialogService.showDialog(
          this.dialog,
          this.translationService.getIngredientCSVTemplatesText(),
          successMsg,
          undefined,
          this.router,
          'settings',
        )
      }

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Resets form data
   */
  onReset() {
    if (!this.isEditMode) {
      this.templateForm.reset()
    } else {
      this.setFields()
    }
  }
}
